import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { ILayoutItemFilter, IUserFilterConfig } from '@dbl/core/dashboard';

const TOP_FILTERS_SELECTORS = {
  generalWrapSortable: '.dbl-dashboard-filter-wrap-sortable',
  userFilter: '.dbl-dashboard-user-filter',
};

interface ICollapseOptions {
  enableCollapseFilter: boolean;
  generalFilters: ILayoutItemFilter[];
  userFilter?: IUserFilterConfig;
  locale: string;
}

interface ICollapseState {
  isCollapsed: boolean;
  showToggleButton: boolean;
}

interface IUseTopFiltersCollapseResult extends ICollapseState {
  setIsCollapsed: (next: boolean | ((prev: boolean) => boolean)) => void;
}

/**
 * 顶部筛选器折叠/展开逻辑：
 * - 根据 General/User 两个区域是否出现多行来决定是否展示 More/Collapse 按钮
 * - 监听宽度变化（ResizeObserver / window.resize）自适应按钮显隐
 * - 持有 isCollapsed 与 showToggleButton 两个渲染状态
 */
export const useTopFiltersCollapse = (
  wrapRef: RefObject<HTMLDivElement>,
  options: ICollapseOptions,
): IUseTopFiltersCollapseResult => {
  const {
    enableCollapseFilter,
    generalFilters,
    userFilter,
    locale,
  } = options;

  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [showToggleButton, setShowToggleButton] = useState<boolean>(false);

  const calcNeedToggle = useCallback((): boolean => {
    if (!enableCollapseFilter) {
      return false;
    }

    const root = wrapRef.current;

    // 根节点还没挂载时，先不展示折叠按钮，等挂载和布局稳定后再由后续刷新逻辑决定
    if (!root) {
      return false;
    }

    /**
     * 编辑态下 General 区会出现双层 `.dbl-dashboard-filter-wrap-sortable`（外层 wrapper + 内层 sortable）。
     * 为了和 TopFilters 里的折叠逻辑保持一致，这里取“最深”的那个作为真实 flex-wrap 容器。
     */
    const generalCandidates = Array.from(
      root.querySelectorAll(TOP_FILTERS_SELECTORS.generalWrapSortable),
    ) as HTMLElement[];
    const generalList = generalCandidates.length
      ? generalCandidates[generalCandidates.length - 1]
      : null;

    const userCandidates = Array.from(
      root.querySelectorAll(TOP_FILTERS_SELECTORS.userFilter),
    ) as HTMLElement[];
    const userList = userCandidates.length
      ? userCandidates[userCandidates.length - 1]
      : null;

    const withAllChildrenVisible = <T,>(container: HTMLElement, measure: () => T): T => {
      const children = Array.from(container.children) as HTMLElement[];
      const prevInlineDisplay = new Map<HTMLElement, string>();
      const prevFlexWrap = container.style.flexWrap;
      const prevOverflowX = container.style.overflowX;

      children.forEach((child) => {
        prevInlineDisplay.set(child, child.style.display);
        child.style.display = '';
      });

      try {
        /**
         * 折叠态下容器会被样式强制 nowrap（用于消除“先换行再隐藏”的闪动）。
         * 但折叠按钮显隐应以“展开态是否会出现多行”为准，因此量测时临时强制 wrap。
         */
        container.style.flexWrap = 'wrap';
        container.style.overflowX = 'visible';
        return measure();
      } finally {
        if (prevFlexWrap) {
          container.style.flexWrap = prevFlexWrap;
        } else {
          container.style.removeProperty('flex-wrap');
        }
        if (prevOverflowX) {
          container.style.overflowX = prevOverflowX;
        } else {
          container.style.removeProperty('overflow-x');
        }
        children.forEach((child) => {
          const prev = prevInlineDisplay.get(child);
          if (prev) {
            child.style.display = prev;
          } else {
            child.style.removeProperty('display');
          }
        });
      }
    };

    /**
     * 多行判断单位用“容器直接子元素”，原因：
     * - 通用筛选器为 `.dbl-filter-drag-wrap`，并非旧逻辑里的 `.dbl-filter-drag`；
     * - 用户筛选器同一行里还包含 `.dbl-filter-vs-button`、Add 按钮等，也都应参与换行判断；
     * - 折叠/展开是通过对子元素写入 `style.display` 实现的，因此量测前需要临时清理 display:none。
     */
    const isMultiRow = (container: HTMLElement | null): boolean => {
      if (!container) return false;

      return withAllChildrenVisible(container, () => {
        const items = Array.from(container.children) as HTMLElement[];
        if (items.length <= 1) return false;

        /**
         * `.dbl-dashboard-user-filter` 使用 `align-items:center`，同一行里不同高度的元素会被垂直居中，导致 top 不一致。
         * 这里在量测阶段临时把 align-items 设为 flex-start 来“对齐到行首”，避免误判为多行。
         */
        const prevAlignItems = container.style.alignItems;
        container.style.alignItems = 'flex-start';

        try {
          const containerRect = container.getBoundingClientRect();
          const rowTops: number[] = [];

          items.forEach((item) => {
            const rect = item.getBoundingClientRect();
            const relativeTop = rect.top - containerRect.top;
            if (!Number.isFinite(relativeTop)) {
              return;
            }
            const normalizedTop = Math.round(relativeTop);
            if (!rowTops.includes(normalizedTop)) {
              rowTops.push(normalizedTop);
            }
          });

          return rowTops.length > 1;
        } catch {
          // 量测失败时降级为“不显示折叠按钮”，避免影响主流程或导致 UI 闪动
          return false;
        } finally {
          if (prevAlignItems) {
            container.style.alignItems = prevAlignItems;
          } else {
            container.style.removeProperty('align-items');
          }
        }
      });
    };

    const generalMulti = isMultiRow(generalList);
    const userMulti = isMultiRow(userList);

    return generalMulti || userMulti;
    /**
     * 这里不把 `generalFilters/userFilter/locale` 放进依赖：
     * - `calcNeedToggle` 的真实输入是 DOM 布局（是否出现多行），而不是数据本身；
     * - 数据变化会触发本 hook 下方的 layout effect 调用 refreshToggleState；
     * - DOM 尺寸变化则由 ResizeObserver/window.resize 触发 refreshToggleState。
     */
  }, [enableCollapseFilter, wrapRef]);

  const refreshToggleState = useCallback(() => {
    const needToggle = calcNeedToggle();

    // 1) 按钮显隐随布局变化，但不重置折叠/展开状态
    setShowToggleButton((prev) => (prev === needToggle ? prev : needToggle));
  }, [calcNeedToggle]);

  useLayoutEffect(() => {
    /**
     * 这里依赖 generalFilters / userFilter / locale，而没有单独展开 DOM 相关依赖：
     * - DOM 结构变化会通过 ResizeObserver / window.resize 触发 refreshToggleState；
     * - 逻辑上依赖的是“是否出现多行”的布局结果，而不是某个具体 DOM 节点引用。
     * 若未来引入新的外部状态参与折叠判断，需要一并审视 calcNeedToggle / refreshToggleState 的依赖设计。
     */
    refreshToggleState();
    const rafId = window.requestAnimationFrame(() => {
      refreshToggleState();
    });
    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [refreshToggleState, generalFilters, userFilter, locale]);

  useEffect(() => {
    if (!enableCollapseFilter) {
      setShowToggleButton(false);
      setIsCollapsed(true);
      return;
    }

    const root = wrapRef.current;
    if (!root) {
      return;
    }

    // 避免在 ResizeObserver 回调里同步 setState 触发布局抖动，改为一帧内合并一次刷新
    let rafId: number | null = null;
    const scheduleUpdate = (): void => {
      if (rafId !== null) {
        return;
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        refreshToggleState();
      });
    };

    let ro: ResizeObserver | null = null;
    let mo: MutationObserver | null = null;
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', scheduleUpdate);
    } else {
      ro = new ResizeObserver(() => {
        scheduleUpdate();
      });
      ro.observe(root);
    }
    /**
     * 仅监听“子节点增删”，用于覆盖以下场景：
     * - 折叠态下删除第一行筛选器：由于隐藏项仍是 display:none，root 尺寸可能不变，ResizeObserver 不触发，导致隐藏项无法补位显示
     * 这里不监听 attributes（如 style 变更），避免与折叠逻辑写入 display 形成反馈回路。
     */
    if (typeof MutationObserver !== 'undefined') {
      mo = new MutationObserver(() => {
        scheduleUpdate();
      });
      mo.observe(root, { childList: true, subtree: true });
    }

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (typeof ResizeObserver === 'undefined') {
        window.removeEventListener('resize', scheduleUpdate);
      } else {
        ro?.disconnect();
      }
      mo?.disconnect();
    };
  }, [enableCollapseFilter, refreshToggleState, wrapRef]);

  return {
    isCollapsed,
    showToggleButton,
    setIsCollapsed,
  };
};

